import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, Search, Users, Check, X, Bed } from "lucide-react";
import { toast } from "sonner";
import {
  adminFetchRsvps,
  adminFetchAccommodations,
  adminFetchStats,
} from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("nb-NO", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const toCsv = (rows, columns) => {
  const escape = (v) => {
    if (v == null) return "";
    const s = Array.isArray(v) ? v.join("; ") : String(v);
    if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((row) => columns.map((c) => escape(row[c.key])).join(","))
    .join("\n");
  return header + "\n" + body;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div
    data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}
    className="bg-[#F3F5E7] border border-[#68919B]/20 p-6"
  >
    <div className="flex items-center justify-between mb-3">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#68919B]">
        {label}
      </p>
      <Icon size={18} strokeWidth={1.3} className={accent || "text-[#68919B]"} />
    </div>
    <p className="font-serif text-4xl text-[#3D5E68] font-light">{value}</p>
  </div>
);

const Table = ({ columns, rows, emptyMsg, testId }) => {
  if (!rows.length) {
    return (
      <div className="text-center py-16 text-[#5A7682] font-light italic">
        {emptyMsg}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-[#68919B]/20 bg-[#F3F5E7]">
      <table data-testid={testId} className="w-full text-sm">
        <thead className="bg-[#EAEDDC]">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#3D5E68] font-medium whitespace-nowrap"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              className={i % 2 === 0 ? "bg-[#F3F5E7]" : "bg-[#EAEDDC]/30"}
            >
              {columns.map((c) => {
                const v = r[c.key];
                return (
                  <td
                    key={c.key}
                    className="px-4 py-3 text-[#3D5E68] font-light align-top"
                  >
                    {c.render
                      ? c.render(v, r)
                      : Array.isArray(v)
                      ? v.join(", ")
                      : v || (
                          <span className="text-[#68919B]/40">—</span>
                        )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AdminSubmissions = () => {
  const [rsvps, setRsvps] = useState([]);
  const [accom, setAccom] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, s] = await Promise.all([
        adminFetchRsvps(),
        adminFetchAccommodations(),
        adminFetchStats(),
      ]);
      setRsvps(r.data || []);
      setAccom(a.data || []);
      setStats(s.data || null);
    } catch (err) {
      console.error(err);
      toast.error("Kunne ikke laste svar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const q = search.trim().toLowerCase();
  const filteredRsvps = useMemo(() => {
    if (!q) return rsvps;
    return rsvps.filter((r) =>
      `${r.name} ${(r.guest_names || []).join(" ")} ${r.email || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [rsvps, q]);

  const filteredAccom = useMemo(() => {
    if (!q) return accom;
    return accom.filter((r) =>
      `${r.name} ${(r.guest_names || []).join(" ")} ${r.email || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [accom, q]);

  const rsvpColumns = [
    { key: "name", label: "Navn" },
    {
      key: "attending",
      label: "Kommer",
      render: (v) =>
        v === "ja" ? (
          <span className="inline-flex items-center gap-1 text-[#3D5E68] font-medium">
            <Check size={14} /> Ja
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[#9E0232]">
            <X size={14} /> Nei
          </span>
        ),
    },
    { key: "guests", label: "Antall" },
    {
      key: "guest_names",
      label: "Øvrige gjester",
      render: (v) => (v && v.length ? v.join(", ") : <span className="text-[#68919B]/40">—</span>),
    },
    { key: "email", label: "E-post" },
    { key: "allergies", label: "Allergier" },
    { key: "message", label: "Hilsen" },
    {
      key: "created_at",
      label: "Sendt inn",
      render: (v) => <span className="whitespace-nowrap">{fmtDate(v)}</span>,
    },
  ];

  const accomColumns = [
    { key: "name", label: "Navn" },
    { key: "email", label: "E-post" },
    { key: "phone", label: "Telefon" },
    { key: "guests", label: "Antall" },
    {
      key: "guest_names",
      label: "Øvrige gjester",
      render: (v) => (v && v.length ? v.join(", ") : <span className="text-[#68919B]/40">—</span>),
    },
    { key: "nights", label: "Netter" },
    { key: "check_in", label: "Innsjekk" },
    { key: "check_out", label: "Utsjekk" },
    { key: "preferences", label: "Preferanser" },
    { key: "message", label: "Melding" },
    {
      key: "created_at",
      label: "Sendt inn",
      render: (v) => <span className="whitespace-nowrap">{fmtDate(v)}</span>,
    },
  ];

  const exportRsvpCsv = () => {
    const csv = toCsv(filteredRsvps, rsvpColumns);
    downloadCsv(`rsvp-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportAccomCsv = () => {
    const csv = toCsv(filteredAccom, accomColumns);
    downloadCsv(`overnatting-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="animate-spin text-[#68919B]" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Users} label="Totalt svar" value={stats.total_rsvps} />
          <StatCard icon={Check} label="Kommer" value={stats.coming} accent="text-[#3D5E68]" />
          <StatCard icon={X} label="Kommer ikke" value={stats.not_coming} accent="text-[#9E0232]" />
          <StatCard icon={Users} label="Total gjester" value={stats.total_guests} />
          <StatCard icon={Bed} label="Overnatting" value={stats.accommodation_requests} />
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 max-w-md">
        <Search size={16} className="text-[#68919B]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="submissions-search"
          placeholder="Søk på navn eller e-post…"
          className="flex-1 bg-transparent border-b border-[#68919B]/40 py-2 text-[#3D5E68] focus:outline-none focus:border-[#3D5E68] transition-colors font-light"
        />
      </div>

      {/* RSVP */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#3D5E68]">
            RSVP-svar ({filteredRsvps.length})
          </h2>
          <button
            onClick={exportRsvpCsv}
            data-testid="export-rsvp-csv"
            className="flex items-center gap-2 px-4 py-2 border border-[#68919B] text-[#68919B] text-[10px] uppercase tracking-[0.25em] hover:bg-[#68919B] hover:text-[#F3F5E7] transition-colors"
          >
            <Download size={14} /> Eksporter CSV
          </button>
        </div>
        <Table
          columns={rsvpColumns}
          rows={filteredRsvps}
          emptyMsg="Ingen RSVP-svar ennå."
          testId="rsvp-table"
        />
      </section>

      {/* Accommodation */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#3D5E68]">
            Overnatting-forespørsler ({filteredAccom.length})
          </h2>
          <button
            onClick={exportAccomCsv}
            data-testid="export-accom-csv"
            className="flex items-center gap-2 px-4 py-2 border border-[#68919B] text-[#68919B] text-[10px] uppercase tracking-[0.25em] hover:bg-[#68919B] hover:text-[#F3F5E7] transition-colors"
          >
            <Download size={14} /> Eksporter CSV
          </button>
        </div>
        <Table
          columns={accomColumns}
          rows={filteredAccom}
          emptyMsg="Ingen overnatting-forespørsler ennå."
          testId="accom-table"
        />
      </section>
    </div>
  );
};
